import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { searchBrands } from '~/api/brandfetch';
import { useDebounce } from '~/hooks/api/useDebounce';
import { BRANDFETCH_SEARCH_QUERY_KEY } from '~/queries/keys';

interface UseBrandfetchSearchProps {
  searchTerm: string;
  enabled?: boolean;
  minLength?: number;
}

export const useBrandfetchSearch = ({
  searchTerm,
  enabled = true,
  minLength = 2,
}: UseBrandfetchSearchProps) => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Use the existing debounce hook
  const debouncedValue = useDebounce(searchTerm, 500);

  useEffect(() => {
    setDebouncedSearchTerm(debouncedValue as string);
  }, [debouncedValue]);

  const shouldFetch = enabled && debouncedSearchTerm.trim().length >= minLength;

  const {
    data: brands = [],
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: [BRANDFETCH_SEARCH_QUERY_KEY, debouncedSearchTerm],
    queryFn: () => searchBrands(debouncedSearchTerm),
    enabled: shouldFetch,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    brands,
    isLoading,
    error,
    isError,
    searchTerm: debouncedSearchTerm,
  };
};
